/**
 * Who is where (§15 routines, §16 proximity rule).
 *
 * V0 hardcodes routines on the card (§20). An NPC is wherever their weekly
 * pattern says they are at this minute, unless they are dead, jailed or
 * gone. No slot matching means they are somewhere you can't reach — which
 * is information, not an error (§15: "the deviation is the story").
 *
 * You cannot interact with anything you can't physically open the door to
 * (§16). `isPresent` is that rule.
 */

import { dayOfWeek } from './time.js';
import type { LocationId, NPC, NpcId, WorldState, WorldTime } from './types/index.js';

export function whereIs(npc: NPC, at: WorldTime): LocationId | null {
  if (npc.status !== 'present') return null;
  const weekday = dayOfWeek(at);
  for (const slot of npc.routine) {
    if (!slot.days.includes(weekday)) continue;
    if (at.minute >= slot.from && at.minute < slot.to) return slot.at;
  }
  return null;
}

/** Every NPC at a location, in a stable order. */
export function npcsAt(state: WorldState, location: LocationId, at: WorldTime = state.time): NpcId[] {
  const ids: NpcId[] = [];
  for (const npc of Object.values(state.npcs)) {
    if (whereIs(npc, at) === location) ids.push(npc.id);
  }
  return ids.sort();
}

/** True when the player is at a door and this NPC is behind it. */
export function isPresent(state: WorldState, npcId: NpcId): boolean {
  const here = state.player.position.at;
  if (here === null) return false;
  const npc = state.npcs[npcId];
  if (!npc) return false;
  return whereIs(npc, state.time) === here;
}
